import {generateBlockId, isStringOrNull, uuidShort8} from "../utils";
import {
    AllowedCalloutChild,
    AllowedTableChild,
    ColorOnlyTransparent,
    ColorOnlyWhite,
    InlineNode,
    MpNode,
    NodeOption
} from "../types";
import {TableBgColorMap, TextBgColorMap, TextColorMap} from "../constants";

export interface ITextStyle {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    delete?: boolean;
    color?: ColorOnlyWhite;
    bgColor?: ColorOnlyTransparent;
}

// 文本
export class TextClass implements MpNode {
    constructor(
        public text: string = '',
        public style?: ITextStyle
    ) {}
    
    toJson() {
        const res = {
            text: this.text,
        } as any;
        if (this.style) {
            Object.assign(res, {
                bold: this.style.bold,
                del: this.style.delete,
                italic: this.style.italic,
                underline: this.style.underline,
                'font-color': TextColorMap[this.style?.color!],
                'bg-color': TextBgColorMap[this.style?.bgColor!],
            });
        }
        return res;
    }
}

// 段落
export class ParagraphClass implements MpNode {
    private readonly children: InlineNode[];
    private readonly options?: NodeOption | null;
    constructor(options?: NodeOption | null, children?: string | InlineNode[]) {
        this.children = typeof children === 'string' ? [new TextClass(children)] : (children || [new TextClass()]);
        this.options = options;
    }
    toJson() {
        return {
            type: 'paragraph',
            blockId: generateBlockId(),
            textIndent: this.options?.indent,
            textAlign: this.options?.align,
            children: this.children.map(child => child.toJson()),
        };
    }
}

// 分级标题
export class HeadingClass implements MpNode {
    private readonly level: number;
    private readonly children: InlineNode[];
    private readonly options?: NodeOption | null;
    
    constructor(level: number, options?: NodeOption | null, children?: string | InlineNode[]) {
        this.level = level;
        children = isStringOrNull(children)
            ? [new TextClass(children || '')]
            : (children as InlineNode[]);
        this.children = children;
        this.options = options;
    }
    
    toJson() {
        return {
            type: 'heading',
            blockId: generateBlockId(),
            level: this.level,
            indent: this.options?.indent,
            textAlign: this.options?.align,
            children: this.children.map(child => child.toJson()),
        };
    }
}

// 超链接
export interface LinkOptions {
    url: string,
    title?: string,
    children?: TextClass[]
}
export class LinkClass implements MpNode {
    private readonly url: string;
    private readonly title: string;
    private readonly children: TextClass[];
    
    constructor(url: string);
    constructor(options?: LinkOptions);
    
    constructor(options?: string | LinkOptions) {
        if (!options) {
            throw new Error('url is required');
        }
        if (typeof options === 'string') {
            this.url = options;
            this.title = options;
            this.children = [new TextClass(options)];
            return;
        }
        const {url, title, children} = options;
        this.url = url;
        this.title = title || url;
        this.children = children || [new TextClass(title || url || '')];
    }
    
    toJson() {
        return {
            type: 'link',
            blockId: generateBlockId(),
            title: this.title,
            href: this.url,
            children: this.children.map(child => child.toJson()),
        };
    }
}

// 表格
export type TableOptions = {
    sticky?: boolean;
    widths?: number[];
} & NodeOption;
export class TableClass implements MpNode {
    private readonly options: TableOptions;
    private readonly children: TableRowClass[] = [];
    
    constructor(
        options: TableOptions | null,
        children: TableRowClass[]
    ) {
        const rows = children.length;
        if (rows === 0) {
            throw new Error('Table must have at least one row');
        }
        options = options || {};
        this.children = children;
        const cols = children[0].children.length;
        options.sticky = !!options.sticky;
        options.widths = options.widths || Array.from({length: cols}, () => 74);
        if (options.widths.length !== cols) {
            throw new Error('widths length must equal cols');
        }
        this.options = options;
    }
    
    toJson() {
        return {
            type: 'table',
            blockId: generateBlockId(),
            renderKey: generateBlockId(),
            sticky: this.options.sticky,
            data: {
                headless: true,
                width: this.options.widths,
            },
            indent: this.options?.indent,
            align: this.options?.align,
            children: this.children.map(child => child.toJson()),
        };
    }
}
// 表格行
export class TableRowClass implements MpNode {
    readonly children: TableCellClass[];
    constructor(children: TableCellClass[]) {
        this.children = children;
    }
    toJson() {
        return {
            type: 'table-row',
            renderKey: generateBlockId(),
            children: this.children.map(child => child.toJson()),
        };
    }
}
// 表格单元格
export interface TableCellOptions {
    rowspan?: number;
    colspan?: number;
    bgColor?: ColorOnlyTransparent;
}
export class TableCellClass implements MpNode {
    private readonly rowspan: number;
    private readonly colspan: number;
    private readonly bgColor?: ColorOnlyTransparent;
    private readonly children: AllowedTableChild[];
    constructor(options?: TableCellOptions | null, children?: string | AllowedTableChild[]) {
        this.rowspan = options?.rowspan ?? 1;
        this.colspan = options?.colspan ?? 1;
        this.bgColor = options?.bgColor;
        this.children = typeof children === 'string'
            ? [new ParagraphClass(null, children)]
            : (children || [new ParagraphClass()]);
    }
    toJson() {
        const bgColor = TableBgColorMap[this.bgColor!];
        return {
            type: 'table-cell',
            renderKey: generateBlockId(),
            data: {
                rowspan: this.rowspan,
                colspan: this.colspan,
                backgroundColor: bgColor,
            },
            children: this.children.map(child => child.toJson()),
        };
    }
}


// 列表
export type ListOptions = {
    depth?: number;
} & Pick<NodeOption, 'align'>;
export class List implements MpNode {
    readonly options?: ListOptions | null;
    private readonly children: TextClass[];
    
    constructor(
        public type: 'ordered-list-item' | 'unordered-list-item',
        options?: ListOptions | null,
        children?: string | TextClass[],
    ) {
        this.options = options;
        this.children = isStringOrNull(children)
            ? [new TextClass(children || '')]
            : (children as TextClass[]);
    }
    toJson() {
        return {
            type: this.type,
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            depth: this.options?.depth ?? 0,
            align: this.options?.align,
            children: this.children.map(child => child.toJson()),
        };
    }
}

// 有序列表
export class OrderedListClass extends List {
    constructor(
        public index: number,
        options?: ListOptions | null,
        children?: string | TextClass[],
    ) {
        super('ordered-list-item', options, children);
    }
    
    toJson() {
        return Object.assign(super.toJson(), {
            index: this.index,
        });
    }
}

// 无序列表
export class UnorderedListClass extends List {
    constructor(
        options?: ListOptions | null,
        children?: string | TextClass[],
    ) {
        super('unordered-list-item', options, children);
    }
}

// 代码块
export type CodeOptions = {
    language?: string;
    autowrap?: boolean;
    title?: string;
    fold?: boolean;
} & Pick<NodeOption, 'indent'>;
export class CodeBlockClass implements MpNode {
    private readonly options: CodeOptions;
    private readonly children: CodeLineClass[];
    
    constructor(options?: CodeOptions | null, children?: CodeLineClass[]) {
        if (!options) {
            this.children = [new CodeLineClass()];
            return;
        }
        this.options = options || {};
        this.children = children ?? [new CodeLineClass()];
    }
    toJson() {
        return {
            type: 'block-code',
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            indent: this.options?.indent,
            language: this.options.language || 'plain',
            autowrap: this.options.autowrap ?? false,
            title: this.options.title ?? "",
            fold: this.options.fold ?? false,
            children: this.children.map(child => child.toJson()),
        };
    }
}
// 代码行
export class CodeLineClass implements MpNode {
    private readonly children: TextClass[];
    constructor(children?: string | TextClass[]) {
        this.children = typeof children === 'string' ? [new TextClass(children)] : (children || [new TextClass()]);
    }
    toJson() {
        return {
            type: 'block-code-line',
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            children: this.children.map(child => child.toJson()),
        };
    }
}

// 高亮块
export type CalloutOptions = {
    theme?: 'info' | 'success' | 'warn' | 'error';
} & Pick<NodeOption, 'indent'>;
export class CalloutClass implements MpNode {
    constructor(
        public options?: CalloutOptions | null,
        public children?: string | AllowedCalloutChild[],
    ) {}
    toJson() {
        const children = typeof this.children === 'string'
            ? [new ParagraphClass(null, this.children)]
            : (this.children || [new ParagraphClass()]);
        return {
            type: 'callout',
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            theme: this.options?.theme || 'info',
            children: children.map(child => child.toJson()),
        }
    }
}

// 阅读统计
export class StatisticsClass implements MpNode {
    constructor(
        public options?: { buttonText?: [string, string]} & Pick<NodeOption, 'indent'> | null
    ) {}
    toJson() {
        return {
            type: 'statistics',
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            buttonTextIndex: this.options?.buttonText ? 6 : undefined,
            customButtonText: this.options?.buttonText,
            indent: this.options?.indent,
            children: [{text: ""}],
        };
    }
}

// 流程图
export type DiagramOptions = {
    id?: string;
    docId?: string;
    boxSize?: {width: number, height: number};
} & NodeOption;
export class DiagramClass implements MpNode {
    constructor(public options?: DiagramOptions | null) {
    }
    toJson() {
        return {
            type: 'diagram',
            id: this.options?.id,
            docId: this.options?.docId,
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            boxSize: this.options?.boxSize || {width: 740, height: 160},
            align: this.options?.align,
            indent: this.options?.indent,
            version: '202503',
            children: [{text: ""}],
        }
    }
}

// 脑图
export type MindmapOptions = {
    id?: string;
    docId?: string;
    boxSize?: {width: number, height: number};
} & NodeOption;
export class MindmapClass implements MpNode {
    constructor(public options?: DiagramOptions | null) {
    }
    toJson() {
        return {
            type: 'mindmap',
            id: this.options?.id,
            docId: this.options?.docId,
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            boxSize: this.options?.boxSize || {width: 740, height: 160},
            align: this.options?.align,
            indent: this.options?.indent,
            version: '202503',
            children: [{text: ""}],
        }
    }
}

// 业务方自行拓展的节点
export class ExtendedNodeClass implements MpNode {
    constructor(
        public type: string,
        public isVoid: boolean,
        public options?: Record<string, any> | null,
        public children?: MpNode[],
    ) {}
    toJson() {
        const children = this.children ?? (this.isVoid ? [new TextClass()] : [new ParagraphClass()])
        return {
            type: this.type,
            blockId: generateBlockId(),
            diffId: uuidShort8(),
            ...this.options,
            children: children.map(child => child.toJson()),
        };
    }
}

// 文档根节点
export interface TitleOption {
    mode?: 'doc' | 'card';
    title?: string;
    showCover?: boolean;
}
export class DocumentClass implements MpNode {
    private readonly children: MpNode[];
    private readonly titleOption?: TitleOption | null
    constructor(titleOption?: TitleOption | null, children?: MpNode[]) {
        this.children = children || [new ParagraphClass()];
        this.titleOption = titleOption;
    }
    toJson() {
        const doc = this.children.map(child => child.toJson());
        if (this.titleOption) {
            const isCard = this.titleOption.mode === 'card';
            doc.unshift({
                type: 'title',
                blockId: generateBlockId(),
                diffId: uuidShort8(),
                slide: isCard,
                theme: isCard ? 'light' : undefined,
                showCover: this.titleOption.showCover,
                children: [{text: this.titleOption.title || '未命名文档'}]
            })
        }
        return doc;
    }
}
